import dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, SerializationContext, DataModelBuilder, SerializedData, isSameData, isSameList } from '../../dto/dto.js';
import { PhoneNumber } from '../phone-number.js';
import { SeatReservationItem } from './seat-reservation-item.js';
import { TimeZone } from '../../../time/timezone.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export enum SeatReservationStatus {
  Live = 'LIVE',
  Sold = 'SOLD',
  Cancelled = 'CANCELLED',
}

export enum SeatReservationLastAction {
  None = 'NONE',
  Sold = 'SOLD',
  PartiallySold = 'PARTIALLY_SOLD',
  Cancelled = 'CANCELLED',
  PartiallyCancelled = 'PARTIALLY_CANCELLED',
  Edited = 'EDITED',
}

export class SeatReservation extends DataModel {
  static readonly TYPE_ID: string = '90f82e6b2d204b95ab13fe035bf10d50';
  static readonly SCHEMA_VERSION: number = 6;

  constructor(
    public readonly uniqueId: string,
    public readonly customerId: string | null,
    public readonly name: string | null,
    public readonly phoneNumber: PhoneNumber | null,
    public readonly note: string | null,
    public readonly expirationDate: dayjs.Dayjs | null,
    public readonly createdBy: string | null,
    public readonly actionBy: string | null,
    public readonly creationDate: dayjs.Dayjs | null,
    public readonly actionDate: dayjs.Dayjs | null,
    public readonly items: SeatReservationItem[],
    public readonly status: SeatReservationStatus = SeatReservationStatus.Live,
    public readonly lastAction: SeatReservationLastAction = SeatReservationLastAction.None,
  ) {
    super();
  }

  get $typeId(): string {
    return SeatReservation.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, SeatReservation.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): SeatReservation {
    return new SeatReservation(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'customerId'),
        MapUtils.getOrNull<string>(map, 'name'),
        PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
        MapUtils.getOrNull<string>(map, 'note'),
        context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'expirationDate')),
        MapUtils.getOrNull<string>(map, 'createdBy'),
        MapUtils.getStringOrNull(map, 'actionBy'),
        context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'creationDate')),
        context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'actionDate')),
        MapUtils.getList<SeatReservationItem>(map, 'items', (e) => {
          return context.fromData(e as SerializedData);
        }),
        toEnumFromString(MapUtils.getString(map, 'status'), SeatReservationStatus),
        toEnumFromString(MapUtils.getString(map, 'lastAction'), SeatReservationLastAction),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'customerId': this.customerId,
      'name': this.name,
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'note': this.note,
      'expirationDate': context.encodeDateOrNull(this.expirationDate),
      'createdBy': this.createdBy,
      'actionBy': this.actionBy,
      'creationDate': context.encodeDateOrNull(this.creationDate),
      'actionDate': context.encodeDateOrNull(this.actionDate),
      'status': fromEnumAsString(this.status),
      'lastAction': fromEnumAsString(this.lastAction),
      'items': this.items.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    customerId = this.customerId,
    name = this.name,
    phoneNumber = this.phoneNumber?.clone() ?? null,
    note = this.note,
    expirationDate = TimeZone.cloneDateOrNull(this.expirationDate),
    createdBy = this.createdBy,
    actionBy = this.actionBy,
    creationDate = TimeZone.cloneDateOrNull(this.creationDate),
    actionDate = TimeZone.cloneDateOrNull(this.actionDate),
    status = this.status,
    lastAction = this.lastAction,
    items = this.items.map((item) => item.clone()),
  }: {
    uniqueId?: string;
    customerId?: string | null;
    name?: string | null;
    phoneNumber?: PhoneNumber | null;
    note?: string | null;
    expirationDate?: dayjs.Dayjs | null;
    createdBy?: string | null;
    actionBy?: string | null;
    creationDate?: dayjs.Dayjs | null;
    actionDate?: dayjs.Dayjs | null;
    status?: SeatReservationStatus;
    lastAction?: SeatReservationLastAction;
    items?: SeatReservationItem[];
  } = {}): this {
    return new SeatReservation(
        uniqueId,
        customerId,
        name,
        phoneNumber,
        note,
        expirationDate,
        createdBy,
        actionBy,
        creationDate,
        actionDate,
        items,
        status,
        lastAction,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.customerId === this.customerId &&
      other.name === this.name &&
      isSameData(other.phoneNumber, this.phoneNumber) &&
      other.note === this.note &&
      other.expirationDate == this.expirationDate &&
      other.createdBy == this.createdBy &&
      other.actionBy == this.actionBy &&
      other.creationDate == this.creationDate &&
      other.actionDate == this.actionDate &&
      other.status === this.status &&
      other.lastAction === this.lastAction &&
      isSameList(other.items, this.items, isSameData);
  }
}

export class SeatReservationBuilder extends DataModelBuilder<SeatReservation> {
  build(context: SerializationContext, map: SerializedData): SeatReservation {
    return SeatReservation.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(SeatReservation.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(SeatReservation.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the following changes were made:
    // - parameter `items` of type `SeatReservationItem[]` was introduced
    if(version < 2) {
      map['items'] ??= [];
      version = 2;
    }

    // In this version the following changes were made:
    // - parameter `name` of type `String` was changed to `Optional<String>`
    // - parameter `customerId` of type `Optional<String>` was introduced
    if(version < 3) {
      version = 3;
    }

    // In this version the following changes were made:
    // - parameter `expirationDate` of type `dayjs.Dayjs | null` was introduced
    if(version < 4) {
      version = 4;
    }

    // In this version the following changes were made:
    // - parameter `createdBy` of type `string | null` was introduced
    // - parameter `cancelledBy` of type `string | null` was introduced
    // - parameter `creationDate` of type `dayjs.Dayjs | null` was introduced
    // - parameter `cancelledDate` of type `dayjs.Dayjs | null` was introduced
    if(version < 5) {
      version = 5;
    }

    // In this version the following changes were made:
    // - parameters `cancelledBy` and `cancelledDate` were renamed to `actionBy` and `actionDate`
    // - parameters `status` and `lastAction` were introduced
    if(version < 6) {
      const isTopLevelCancelled = MapUtils.getIntegerOrNull(map, 'cancelledDate') != null;
      const hasCancelledItem = !isTopLevelCancelled && MapUtils
        .getList<SerializedData>(map, 'items', item => item as SerializedData)
        .some(item => MapUtils.getIntegerOrNull(item, 'cancelledDate') != null);

      map['actionBy'] = MapUtils.getStringOrNull(map, 'cancelledBy');
      map['actionDate'] = MapUtils.getIntegerOrNull(map, 'cancelledDate');
      map['status'] = fromEnumAsString(isTopLevelCancelled ? SeatReservationStatus.Cancelled : SeatReservationStatus.Live);
      map['lastAction'] = fromEnumAsString(isTopLevelCancelled
        ? SeatReservationLastAction.Cancelled
        : hasCancelledItem
          ? SeatReservationLastAction.PartiallyCancelled
          : SeatReservationLastAction.None);
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: SeatReservation,
      map: SerializedData,
  ): SerializedData {
    let version = model.$schemaVersion.to ?? SeatReservation.SCHEMA_VERSION;

    if(version >= 6) {
      version = 5;
      const isLive = model.status === SeatReservationStatus.Live;
      map['cancelledBy'] = isLive ? null : model.actionBy;
      map['cancelledDate'] = isLive ? null : context.encodeDateOrNull(model.actionDate);
      map['$schema_version'] = VersionRange.encodedBetween(5, 6);
    }

    return map;
  }
}
